import { fetchWithAuth } from '../lib/fetchWithAuth';

const API_BASE_URL = import.meta.env.VITE_API_URL?.replace(/\/$/, '');

export type EmployerVerificationStatus = 'pending' | 'approved' | 'rejected' | 'needs_more_info';

export type EmployerChecklistItem = {
  id: string;
  label: string;
  completed: boolean;
  completedAt?: string;
};

export type EmployerDocument = {
  id: string;
  name: string;
  type: 'certificate' | 'company_doc' | 'proof_of_address';
  uploadedAt: string;
  url: string;
};

export type EmployerAuditEvent = {
  id: string;
  action: string;
  actor: string;
  createdAt: string;
  note?: string;
};

export type AdminEmployerProfile = {
  id: string;
  companyName: string;
  contactName: string;
  contactEmail: string;
  status: EmployerVerificationStatus;
  submittedAt: string;
  lastUpdated: string;
  notes?: string;
  checklist: EmployerChecklistItem[];
  documents: EmployerDocument[];
  auditLog: EmployerAuditEvent[];
};

export type EmployerFilters = {
  status?: EmployerVerificationStatus;
  query?: string;
};

const mockEmployers: AdminEmployerProfile[] = [
  {
    id: 'emp-5001',
    companyName: 'Harbor Wellness Group',
    contactName: 'Sophie Davies',
    contactEmail: 'sophie@harborwellness.co.uk',
    status: 'pending',
    submittedAt: '2025-11-12T08:00:00.000Z',
    lastUpdated: '2025-11-12T08:00:00.000Z',
    checklist: [
      { id: 'kyc-company-doc', label: 'Company documents uploaded', completed: true, completedAt: '2025-11-12T08:02:00.000Z' },
      { id: 'kyc-id-check', label: 'Director ID verified', completed: false },
      { id: 'kyc-safeguarding', label: 'Safeguarding policy reviewed', completed: false },
    ],
    documents: [
      { id: 'doc-9001', name: 'Companies House Certificate.pdf', type: 'company_doc', uploadedAt: '2025-11-12T08:01:00.000Z', url: '#' },
    ],
    auditLog: [],
  },
  {
    id: 'emp-5002',
    companyName: 'Bright Future Care',
    contactName: 'Marcus Shaw',
    contactEmail: 'marcus@brightfuturecare.uk',
    status: 'needs_more_info',
    submittedAt: '2025-11-10T10:45:00.000Z',
    lastUpdated: '2025-11-11T12:10:00.000Z',
    notes: 'Waiting on updated safeguarding policy.',
    checklist: [
      { id: 'kyc-company-doc', label: 'Company documents uploaded', completed: true, completedAt: '2025-11-10T10:50:00.000Z' },
      { id: 'kyc-id-check', label: 'Director ID verified', completed: true, completedAt: '2025-11-10T11:20:00.000Z' },
      { id: 'kyc-safeguarding', label: 'Safeguarding policy reviewed', completed: false },
    ],
    documents: [
      { id: 'doc-9002', name: 'ID_Verification.png', type: 'certificate', uploadedAt: '2025-11-10T11:00:00.000Z', url: '#' },
    ],
    auditLog: [
      { id: 'audit-501', action: 'needs_more_info', actor: 'admin:ella', createdAt: '2025-11-11T12:10:00.000Z', note: 'Awaiting updated policy with signatures.' },
    ],
  },
];

const filterMockEmployers = (filters: EmployerFilters = {}) => {
  return mockEmployers.filter((profile) => {
    if (filters.status && profile.status !== filters.status) return false;
    if (filters.query) {
      const needle = filters.query.toLowerCase();
      const haystack = [profile.companyName, profile.contactName, profile.contactEmail].join(' ').toLowerCase();
      if (!haystack.includes(needle)) return false;
    }
    return true;
  });
};

export async function fetchAdminEmployers(filters: EmployerFilters = {}): Promise<AdminEmployerProfile[]> {
  if (!API_BASE_URL) {
    throw new Error('VITE_API_URL is required to load admin employers.');
  }
  const params = new URLSearchParams();
  if (filters.status) params.set('status', filters.status);
  if (filters.query) params.set('q', filters.query);
  const response = await fetchWithAuth(`${API_BASE_URL}/v1/admin/employers?${params.toString()}`);
  if (!response.ok) {
    const text = await response.text().catch(() => 'request failed');
    throw new Error(`Employer verification fetch failed (${response.status}): ${text}`);
  }
  const payload = await response.json();
  return (payload?.employers as AdminEmployerProfile[]) ?? [];
}

export async function updateEmployerStatus(id: string, status: EmployerVerificationStatus, note?: string) {
  if (!API_BASE_URL) {
    throw new Error('VITE_API_URL is required to update employer status.');
  }
  const response = await fetchWithAuth(`${API_BASE_URL}/v1/admin/employers/${id}/status`, {
    method: 'POST',
    body: JSON.stringify({ status, note }),
  });
  if (!response.ok) {
    const text = await response.text().catch(() => 'request failed');
    throw new Error(`Employer status update failed (${response.status}): ${text}`);
  }
  return (await response.json()) as AdminEmployerProfile;
}

export async function toggleChecklistItem(id: string, checklistId: string, completed: boolean) {
  if (!API_BASE_URL) {
    throw new Error('VITE_API_URL is required to update employer checklist.');
  }
  const response = await fetchWithAuth(`${API_BASE_URL}/v1/admin/employers/${id}/checklist`, {
    method: 'POST',
    body: JSON.stringify({ checklistId, completed }),
  });
  if (!response.ok) {
    const text = await response.text().catch(() => 'request failed');
    throw new Error(`Checklist update failed (${response.status}): ${text}`);
  }
  return (await response.json()) as AdminEmployerProfile;
}
